/**
 * UAT-FB#99 / ADR-001 (2026-06-17): 统一 backend KV 同步 composable.
 *
 * <p>设计目标: 用一套 API 同时适配前端 2 种 store 范式 — Pinia defineStore + 自管 reactive.
 *
 * <h3>用法 A — 自管 reactive store</h3>
 * <pre>
 *   // stores/attConfig.ts (改造前: const shifts = reactive(loadStorage() || SEED))
 *   import { useBackendKv } from './_backendKv'
 *
 *   const cfg = useBackendKv('cfg:att_config', {
 *     shifts: [...SEED_SHIFTS],
 *     workPatterns: [...SEED_PATTERNS],
 *     // ...其余字段同步管理
 *   })
 *   // 导出仍是 reactive proxy, 对外 API 不变, 调用方 .push() / [i]=x 都触发同步
 *   export const shifts = cfg.state.shifts
 *   export const workPatterns = cfg.state.workPatterns
 * </pre>
 *
 * <h3>用法 B — Pinia defineStore</h3>
 * <pre>
 *   // stores/roleStore.ts (Pinia setup 风格)
 *   export const useRoleStore = defineStore('role', () => {
 *     const cfg = useBackendKv('cfg:role_store', { roles: [], audits: [] })
 *     return { ...toRefs(cfg.state), loaded: cfg.loaded, save: cfg.save }
 *   })
 * </pre>
 *
 * <h3>持久化语义</h3>
 *   1. 创建时立刻 GET /sys-kv/{key}, 拿到远端用 Object.assign 覆盖默认 state
 *   2) 远端不存在 — state 保持默认, 首次 mutation 触发 PUT 创建 (version=0)
 *   3) state 任意 mutation — debounce 800ms 后 PUT (乐观锁 version)
 *   4) 409 version 冲突 — 立刻再 GET 拉最新, 用户下次操作基于最新值
 *   5) 未登录 — 完全不同步 (保持默认), 登录后页面 reload 触发再次注册
 */
import { reactive, ref, watch, type Ref } from 'vue'
import { sysKvApi } from '@/api'
import { useUserStore } from './user'

// ─── 同 key 共享 pull 注册表 ──────────────────────────────────────
//   问题: 同一个 sys-kv key 常被多个组件/store 实例各自 useBackendKv → 各 pull 一发,
//     且这些 pull 常是「串行」发生 (一个回来下一个才发), api/request.ts 的并发去重抓不到.
//   方案: 模块级短 TTL 缓存「同 key 的 pull promise」, 收敛同一次页面加载内的重复读.
//     - 只缓存自动 pull; reload(force=true) 强制绕过, 保证「手动刷新」拿最新
//     - flush (PUT) 成功后失效, 下个读者读到含自身写入的最新值
//     - 失败不缓存, 允许重试
const PULL_CACHE_MS = 5000
interface SharedPull { promise: Promise<any>; ts: number }
const pullRegistry = new Map<string, SharedPull>()

function sharedPull(key: string, force: boolean, silent = false): Promise<any> {
  if (!force) {
    const hit = pullRegistry.get(key)
    if (hit && Date.now() - hit.ts < PULL_CACHE_MS) return hit.promise
  }
  const promise = sysKvApi.get(key, silent ? { silent: true } : undefined)
  pullRegistry.set(key, { promise, ts: Date.now() })
  promise.catch(() => {
    if (pullRegistry.get(key)?.promise === promise) pullRegistry.delete(key)
  })
  return promise
}

function invalidatePull(key: string): void {
  pullRegistry.delete(key)
}

export interface UseBackendKvOptions {
  /** 默认 800ms — 操作频繁的 store 可调大 */
  debounceMs?: number
  /** 不参与同步的字段 (临时 UI 状态等) */
  exclude?: string[]
  /** 自定义序列化 — 可剔除衍生字段 */
  serialize?: (state: any) => any
  /** 自定义反序列化 — 远端值合并入 state 前的转换 */
  deserialize?: (remote: any, currentState: any) => any
  /** 拒绝远端值的检查器 — 返 true 表示该值不可信, 不用它覆盖 (常用于 schema 升级期) */
  reject?: (remote: any) => boolean
  /**
   * 当前授权上下文是否允许同步该 key。用于只对特定生效角色开放的敏感配置；
   * false 时不发 GET/PUT，使用本地安全默认值，避免角色切换后产生预期内 403。
   */
  canSync?: (userStore: { token: string; user?: any }) => boolean
  /** 同步失败由 store 自己降级时，不弹全局错误提示。 */
  silent?: boolean
}

export interface BackendKvHandle<T extends object> {
  /** 响应式 state, 直接读写 */
  state: T
  /** 远端拉取完否 */
  loaded: Ref<boolean>
  /** 错误消息 (PUT 失败/拉取失败等) */
  error: Ref<string | null>
  /** 强制立刻刷写, 跳过 debounce */
  save: () => Promise<void>
  /** 强制重拉远端 */
  reload: () => Promise<void>
  /** 用其他权威数据源更新当前内存状态，不触发 sys_kv 回写 */
  hydrate: (patch: Partial<T>) => void
  /** 当前 version (调试用) */
  version: Ref<number>
}

/**
 * 创建一个 backend KV 同步的 reactive state.
 * 调用方拿到的 state 是 reactive proxy, 对它的任意 mutation 都会自动 debounced PUT 到后端.
 */
export function useBackendKv<T extends object>(
  key: string,
  defaults: T,
  opts: UseBackendKvOptions = {},
): BackendKvHandle<T> {
  if (typeof window === 'undefined') {
    // SSR 安全 fallback — 直接返默认 state, 不做同步
    const ssrState = reactive(deepClone(defaults)) as T
    return {
      state: ssrState,
      loaded: ref(true),
      error: ref(null),
      save: async () => {},
      reload: async () => {},
      hydrate: (patch: Partial<T>) => { applyPatch(ssrState, patch) },
      version: ref(0),
    }
  }

  const state = reactive(deepClone(defaults)) as T
  const loaded = ref(false)
  const error = ref<string | null>(null)
  const version = ref(0)
  const debounce = opts.debounceMs ?? 800
  let timer: any = null
  // 脏检查基线: 上次「与服务器同步过」的序列化值. null = 尚无远端值 (首次 mutation 应创建).
  let lastSyncedJson: string | null = null

  const serializePayload = (): any =>
    opts.serialize ? opts.serialize(state) : stripFunctions(state, opts.exclude)

  const pull = async (force = false): Promise<void> => {
    const userStore = safeGetUserStore()
    if (!userStore?.token || (opts.canSync && !opts.canSync(userStore))) {
      loaded.value = true
      error.value = null
      return
    }
    try {
      const kv: any = await sharedPull(key, force, opts.silent === true)
      if (kv && kv.value != null && !(opts.reject && opts.reject(kv.value))) {
        const patch = opts.deserialize ? opts.deserialize(kv.value, state) : kv.value
        applyPatch(state, patch)
        version.value = kv.version ?? 0
        // 记基线: 此刻 state 即服务器已有值, 后续「加载即重算」若产出相同值则不再写回
        lastSyncedJson = JSON.stringify(serializePayload())
      }
      loaded.value = true
      error.value = null
    } catch (e: any) {
      loaded.value = true
      error.value = e?.message || String(e)
      console.warn(`[backendKv] GET ${key} 失败:`, error.value)
    }
  }

  const flush = async (): Promise<void> => {
    const userStore = safeGetUserStore()
    if (!userStore?.token || (opts.canSync && !opts.canSync(userStore))) return
    const payload = serializePayload()
    const json = JSON.stringify(payload)
    // 脏检查: 与上次同步值完全一致 → 无实质变更, 跳过 PUT.
    //   根治「pull 回来 → 组件重算派生字段 → deep watch 误判为编辑 → 写回」的写放大.
    //   绝不会跳过真正改了数据的写 (json 不同则照常 PUT).
    if (json === lastSyncedJson) return
    try {
      const r: any = await sysKvApi.put(key, payload, version.value)
      version.value = r?.version ?? version.value + 1
      lastSyncedJson = json
      invalidatePull(key)   // 写后失效共享 pull, 下个读者拿到含本次写入的最新值
      error.value = null
    } catch (e: any) {
      const code = e?.response?.status || e?.code
      if (code === 409) {
        // 版本冲突 — 立刻强制重拉 (绕过共享缓存), 用户下次操作基于最新值
        await pull(true)
        error.value = '配置已被其他用户更新，已为你刷新最新值，请重新确认后保存'
      } else {
        error.value = e?.message || String(e)
        console.warn(`[backendKv] PUT ${key} 失败:`, error.value)
      }
    }
  }

  // 启动: 未登录则不同步, 已登录则 pull → watch
  // 关键: useUserStore() 必须 lazy 调 — 大量 store 在模块顶层调 useBackendKv, 此时 Pinia 可能还没 install
  function safeGetUserStore(): { token: string; user?: any } | null {
    try { return useUserStore() } catch { return null }
  }
  const initUserStore = safeGetUserStore()
  if (!initUserStore || !initUserStore.token) {
    loaded.value = true
    // 仍注册 watch — 登录后页面 reload, 再次进入时会有 token
    setupWatch()
  } else {
    pull().then(setupWatch)
  }

  function setupWatch() {
    watch(state, () => {
      if (!loaded.value) return
      const us = safeGetUserStore()
      if (!us || !us.token) return // 未登录不写
      if (opts.canSync && !opts.canSync(us)) return // 当前生效角色无权同步该敏感配置
      if (timer) clearTimeout(timer)
      timer = setTimeout(flush, debounce)
    }, { deep: true })
  }

  const hydrate = (patch: Partial<T>): void => {
    applyPatch(state, patch)
    // watcher 会在微任务阶段观察到本次变更；先更新基线，届时 flush 会识别为无实质编辑并跳过 PUT。
    lastSyncedJson = JSON.stringify(serializePayload())
    if (timer) {
      clearTimeout(timer)
      timer = null
    }
  }

  return {
    state,
    loaded,
    error,
    save: async () => {
      if (timer) {
        clearTimeout(timer)
        timer = null
      }
      await flush()
    },
    reload: () => pull(true),   // 手动刷新强制绕过共享缓存
    hydrate,
    version,
  }
}

/** 把 patch 内容浅合并入 reactive state — 保持 state 是同一 reactive proxy */
function applyPatch(state: any, patch: any): void {
  if (patch == null || typeof patch !== 'object') return
  for (const k of Object.keys(patch)) {
    const v = patch[k]
    if (Array.isArray(v)) {
      // 数组: 整体替换 (保持 reactive 的 splice 触发性)
      const arr = state[k]
      if (Array.isArray(arr)) {
        arr.splice(0, arr.length, ...v)
      } else {
        state[k] = v
      }
    } else if (v != null && typeof v === 'object' && !Array.isArray(v)
      && state[k] != null && typeof state[k] === 'object' && !Array.isArray(state[k])) {
      // 嵌套对象递归 merge
      applyPatch(state[k], v)
    } else {
      state[k] = v
    }
  }
}

function deepClone<T>(x: T): T {
  return JSON.parse(JSON.stringify(x))
}

function stripFunctions(state: any, exclude?: string[]): any {
  return JSON.parse(JSON.stringify(state, (k, v) => {
    if (exclude && exclude.includes(k)) return undefined
    if (typeof v === 'function') return undefined
    return v
  }))
}
